import { omit } from "lodash"
import React from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
// import BootstrapTable from "react-bootstrap-table-next";
import { useTranslation } from "react-i18next"
import { UpdateUserRequest, UserModel } from "../../api/user.api"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { updateUser } from "../../store/user.store"

function UsersTable({
    users,
    userDetails,
    setUserDetails,
    setShow
}: {
    users: UserModel[]
    userDetails: Record<string, any>
    setUserDetails: Function
    setShow: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const usersColumns = [
        {
            dataField: "id",
            text: t("#")
        },
        {
            text: t("User_Id"),
            dataField: "id"
        },
        {
            text: t("Email"),
            dataField: "email"
        },
        {
            text: t("Full name"),
            dataField: "full_name"
        },
        {
            text: t("Phone"),
            dataField: "phone"
        },
        {
            text: t("Status"),
            dataField: "active",
            formatter: (cell: number) => {
                return Number(cell) === 1 ? (
                    <span className="status">{t("Active")}</span>
                ) : (
                    <span className="expired"> {t("Inactive")} </span>
                )
            },
            csvFormatter: (cell: number, row: Record<string, any>[]) => {
                return cell === 1 ? t("Active") : t("Inactive")
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Edit", "Activate/Deactivate"].map((action) => (
                                <Dropdown.Item
                                    key={
                                        action !== "Activate/Deactivate"
                                            ? action
                                            : row.active
                                              ? "Deactivate"
                                              : "Activate"
                                    }
                                    className="py-0"
                                    onClick={() => {
                                        if (action !== "Activate/Deactivate") {
                                            setUserDetails(
                                                omit(
                                                    {
                                                        ...row,
                                                        groups: [row.ParcelHubUserGroup[0]?.Group["id"]],
                                                        warehouses: row.ParcelHubUserWarehouse?.map(
                                                            (uw: Record<string, any>) => uw.Warehouse["id"]
                                                        )
                                                    },
                                                    "password"
                                                )
                                            )

                                            setShow(true)
                                        } else {
                                            dispatch(
                                                updateUser(
                                                    omit(
                                                        { ...row, active: !row.active },
                                                        "password"
                                                    ) as UpdateUserRequest
                                                )
                                            )
                                        }
                                    }}>
                                    {action !== "Activate/Deactivate"
                                        ? t(action)
                                        : row.active
                                          ? t("Deactivate")
                                          : t("Activate")}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        }
    ]

    const expandRow = {
        renderer: (row: any): JSX.Element => {
            return (
                <Row key={`expanding-${row?.username}`} className="expanding-row px-4 py-4">
                    <Col xs={12} sm={12} md={12} lg={4}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5}>
                                <h1 className="label mb-2">{t("Warehouses")}</h1>
                                {/* <Col xs={12} sm={12} md={12} lg={7} className="value"> */}
                                {row?.ParcelHubUserWarehouse?.map((uw: Record<string, any>) => (
                                    <p>{uw?.Warehouse?.name || "-"}</p>
                                ))}
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5}>
                                <h1 className="label mb-2">{t("Groups")}</h1>
                                {/* <Col xs={12} sm={12} md={12} lg={7} className="value"> */}
                                {row?.ParcelHubUserGroup?.map((ug: Record<string, any>) => (
                                    <p>{ug?.Group?.name}</p>
                                ))}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon disabled" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon disabled" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {usersColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={users}
                    columns={usersColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "UsersTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search users-search">
                                <Col xs={12} sm={12} md={12} lg={12} className="d-flex justify-content-end">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                <Col className="d-none">
                                    {/* <ExportCSVButton {...props.csvProps} id="usersExportButton">
                        Export
                    </ExportCSVButton> */}
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        expandRow={expandRow}
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{ dataField: "email_verified_at", order: "desc" }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default UsersTable
